/**
 * `Letterhead` — the demo's `letterhead()` (demo line 1488): the OEM's logo on
 * the left, the legal name, the issuing unit and the document reference on the
 * right, over an accent rule, then a mono document-control strip.
 *
 * Brand-aware: the logo and the legal name follow the selected brand through
 * `useBrand()`. The class names (`letterhead`, `doccontrol`) are stable hooks
 * for the report download's stylesheet (`modules/reports/report-export.ts`).
 *
 * UI layer (layer 4 of the 4-layer architecture).
 *
 * @see ./doc-identity.ts
 */

import { OEM_PRODUCT } from './doc-identity';
import { OemLogo } from './OemLogo';
import { useOemDocIdentity } from './useOemDocIdentity';

export interface LetterheadProps {
  /**
   * The document was generated by the product rather than issued by a person:
   * the second line names the product instead of the division.
   */
  product?: boolean;
  docType: string;
  docId?: string;
  rev?: string;
  /** As printed — "09 Sep 2026", "09 Sep 2026 06:00". */
  date?: string;
  classification?: string;
  /** Overrides the division line — a site document adopted from a customer. */
  unit?: string | null;
}

export function Letterhead({
  product = false,
  docType,
  docId = '',
  rev = '',
  date = '',
  classification = 'Internal — customer shareable',
  unit = null,
}: LetterheadProps) {
  const oem = useOemDocIdentity();
  const second = product ? `${OEM_PRODUCT.name} · ${OEM_PRODUCT.desc}` : (unit ?? oem.division);
  const reference = `${docType}${docId ? ` · ${docId}` : ''}${rev ? ` Rev ${rev}` : ''}`;

  return (
    <>
      <div
        className="letterhead mb-3 flex items-start justify-between gap-4 pb-2.5"
        style={{ borderBottom: '2px solid var(--accent)' }}
      >
        <div className="lh-logo">
          <OemLogo height={30} />
        </div>
        <div
          className="lh-right text-right text-[11px] leading-[1.4]"
          style={{ color: 'var(--ink-tertiary)' }}
        >
          <b className="text-[11.5px]" style={{ color: 'var(--ink-primary)' }}>
            {oem.legal}
          </b>
          <br />
          {second}
          <br />
          {reference}
        </div>
      </div>
      {docId || date ? (
        <div
          className="doccontrol mono -mt-1 mb-3 flex flex-wrap gap-x-3.5 gap-y-1 text-[10.5px]"
          style={{ color: 'var(--ink-tertiary)' }}
        >
          {docId ? <span>Document {docId}</span> : null}
          {rev ? <span>Rev {rev}</span> : null}
          {date ? <span>Issued {date}</span> : null}
          <span>{classification}</span>
        </div>
      ) : null}
    </>
  );
}
